// components/TeamEditPopup.js
import React, { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "react-i18next";
import {
  createLawImplementationProsecutorAssignment,
  deleteLawImplementationProsecutorAssignment,
  updateLawImplementationProsecutorAssignment,
  updateTeamName,
} from "services/lawImplementationService";
import { getEmployeesByAdminAndOrg, isFollowUpProsecutor } from "services/getEmployees";
import { getInitials } from "utils/helpers";
import { X, Trash2, Award, Shield, UserPlus, Search, RefreshCw } from "lucide-react";

interface Member {
  id: string;
  user_id: string;
  user_name: string;
  isTeamLeader: boolean;
}

interface EmployeeData {
  user: { id: number | string; first_name: string; last_name?: string };
  roles: string[];
  department?: string;
}

const TeamEditPopup = ({
  teamData,
  onClose,
  onSuccess,
}: {
  teamData: any;
  onClose: () => void;
  onSuccess: (responses: any) => void;
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const contextData = useAuth();

  const teamRecord = teamData?.followup_team_name?.[0] || {};
  // Each member is linked to the request via followup / audit_folloup + team_type.
  const linkField = teamData?.audit_folloup ? "audit_folloup" : "followup";
  const linkValue =
    teamData?.audit_folloup || teamData?.followup || teamRecord?.followup || "";
  // FollowUpTeam.team_type only accepts "prosecutor" | "audit".
  const teamType =
    teamData?.team_type ||
    teamData?.prosecutors?.[0]?.team_type ||
    (teamData?.audit_folloup ? "audit" : "prosecutor");

  const [submitting, setSubmitting] = useState(false);
  const [teamName, setTeamName] = useState(teamRecord.team_name || "");
  const [members, setMembers] = useState<Member[]>(
    (teamData?.prosecutors ?? []).map((m: any) => ({
      id: String(m.id),
      user_id: String(m.user_id),
      user_name: m.user_name,
      // FollowUpTeam stores leadership in `role`; accept an is_team_leader flag too.
      isTeamLeader:
        m.role === "leader" || m.is_team_leader === true || m.is_team_leader === "true",
    }))
  );
  const [showAddMember, setShowAddMember] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState<EmployeeData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeData | null>(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Fetch available prosecutors when adding a member
  useEffect(() => {
    if (!showAddMember) return;
    setLoadingEmployees(true);
    getEmployeesByAdminAndOrg({
      bearer:
        contextData.token ??
        contextData?.user?.access_token ??
        contextData?.user?.user?.access_token,
      admin_unit: contextData?.user?.admin_unit || "",
      org_unit: contextData?.user?.org_unit || "",
    })
      .then((data: EmployeeData[]) => {
        const prosecutors = data.filter((emp) => isFollowUpProsecutor(emp));
        const currentIds = members.map((m) => m.user_id);
        setAvailableEmployees(
          prosecutors.filter((emp) => !currentIds.includes(String(emp.user.id)))
        );
      })
      .catch((error) => {
        console.error("Error fetching employees:", error);
        showToast(t("FailedToFetchEmployees") || "Failed to fetch employees.", "error");
      })
      .finally(() => setLoadingEmployees(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddMember]);

  const handleSaveName = async () => {
    if (!teamRecord.id || teamName === teamRecord.team_name) return;
    setSubmitting(true);
    try {
      await updateTeamName({
        id: teamRecord.id,
        payload: {
          id: teamRecord.id,
          team_name: teamName,
          followup: teamData.followup,
          audit_folloup: teamRecord.audit_folloup ?? teamData.audit_folloup,
        },
      });
      showToast(t("TeamUpdatedSuccess") || "Team updated successfully!", "success");
      onSuccess?.([]);
    } catch (error) {
      console.error("Error updating team name:", error);
      showToast(t("FailedToUpdateTeam") || "Failed to update team.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetLeader = async (member: Member) => {
    setSubmitting(true);
    try {
      const currentLeader = members.find((m) => m.isTeamLeader && m.id !== member.id);
      if (currentLeader) {
        const fd = new FormData();
        fd.append("is_team_leader", "false");
        fd.append("role", "member");
        await updateLawImplementationProsecutorAssignment(currentLeader.id, fd);
      }
      const fd = new FormData();
      fd.append("is_team_leader", "true");
      fd.append("role", "leader");
      await updateLawImplementationProsecutorAssignment(member.id, fd);
      setMembers((prev) => prev.map((m) => ({ ...m, isTeamLeader: m.id === member.id })));
      showToast(t("TeamLeaderUpdatedSuccess") || "Team leader updated.", "success");
      onSuccess?.([]);
    } catch (error) {
      console.error("Error updating team leader:", error);
      showToast(t("FailedToUpdateTeamLeader") || "Failed to update team leader.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (member: Member) => {
    if (!window.confirm(t("ConfirmRemoveTeamMember") || "Remove this team member?")) return;
    setSubmitting(true);
    try {
      await deleteLawImplementationProsecutorAssignment(member.id);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      showToast(t("TeamMemberRemovedSuccess") || "Team member removed.", "success");
      onSuccess?.([]);
    } catch (error) {
      console.error("Error removing team member:", error);
      showToast(t("FailedToRemoveTeamMember") || "Failed to remove team member.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedEmployee) {
      showToast(t("PleaseSelectTeamMember") || "Please select a team member.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const fullName = `${selectedEmployee.user.first_name} ${
        selectedEmployee.user.last_name || ""
      }`.trim();
      const fd = new FormData();
      fd.append(linkField, String(linkValue));
      fd.append("team_type", teamType);
      // Link the new member to its named team (as createNewRequest does), otherwise
      // the member is not found by team membership lookups.
      if (teamRecord?.id) fd.append("followup_team", String(teamRecord.id));
      fd.append("user_id", String(selectedEmployee.user.id));
      fd.append("user_name", fullName);
      fd.append("is_team_leader", "false");
      fd.append("role", "member");
      const res = await createLawImplementationProsecutorAssignment(fd);
      setMembers((prev) => [
        ...prev,
        {
          id: String(res?.id ?? selectedEmployee.user.id),
          user_id: String(selectedEmployee.user.id),
          user_name: fullName,
          isTeamLeader: false,
        },
      ]);
      setShowAddMember(false);
      setSelectedEmployee(null);
      setSearchQuery("");
      showToast(t("TeamMemberAddedSuccess") || "Team member added.", "success");
      onSuccess?.([]);
    } catch (error) {
      console.error("Error adding team member:", error);
      showToast(t("FailedToAddTeamMember") || "Failed to add team member.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredEmployees = availableEmployees.filter((emp) =>
    `${emp.user.first_name} ${emp.user.last_name || ""}`
      .trim()
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("EditTeam") || "Edit Team"}
            {teamRecord.team_name ? ` - ${teamRecord.team_name}` : ""}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {/* Team name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">{t("TeamName") || "Team Name"}</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={t("EnterTeamNamePlaceholder") || "Enter team name"}
              />
              <button
                onClick={handleSaveName}
                disabled={submitting || teamName === teamRecord.team_name}
                className="px-4 py-2 rounded-lg bg-[#212121] text-white text-sm hover:bg-gray-800 disabled:opacity-40 transition-colors"
              >
                {t("Save") || "Save"}
              </button>
            </div>
          </div>

          {/* Add member */}
          {showAddMember ? (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">{t("AddNewTeamMember") || "Add New Team Member"}</h3>
              {loadingEmployees ? (
                <div className="text-center py-4 text-gray-500">{t("Loading") || "Loading..."}</div>
              ) : (
                <>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t("SearchProsecutors") || "Search prosecutors..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto mb-3 border rounded-lg">
                    {filteredEmployees.length === 0 ? (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        {t("NoAvailableProsecutorsFound") || "No available prosecutors found."}
                      </div>
                    ) : (
                      filteredEmployees.map((emp) => (
                        <div
                          key={emp.user.id}
                          onClick={() => setSelectedEmployee(emp)}
                          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-100 ${
                            String(selectedEmployee?.user.id) === String(emp.user.id) ? "bg-green-50" : ""
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium">
                            {getInitials(`${emp.user.first_name} ${emp.user.last_name || ""}`)}
                          </div>
                          <p className="font-medium text-sm">{emp.user.first_name} {emp.user.last_name}</p>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowAddMember(false); setSelectedEmployee(null); setSearchQuery(""); }}
                      className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      {t("Cancel") || "Cancel"}
                    </button>
                    <button
                      onClick={handleAdd}
                      disabled={!selectedEmployee || submitting}
                      className="flex-1 py-2 px-4 bg-[#212121] text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                    >
                      {submitting ? t("Adding") || "Adding..." : t("AddMember") || "Add Member"}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex justify-end mb-3">
              <button
                onClick={() => setShowAddMember(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <UserPlus className="w-4 h-4" />
                {t("AddMember") || "Add Member"}
              </button>
            </div>
          )}

          {/* Member list */}
          {members.length === 0 ? (
            <div className="text-center py-8 text-gray-500">{t("NoTeamMembersFound") || "No team members found."}</div>
          ) : (
            <div className="space-y-3">
              {members.map((member) => (
                <div
                  key={member.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    member.isTeamLeader ? "border-green-500 bg-green-50" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm font-medium">
                      {getInitials(member.user_name)}
                    </div>
                    <p className="font-medium text-gray-900">{member.user_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {member.isTeamLeader && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        <Award className="w-3 h-3" />
                        {t("TeamLeader") || "Team Leader"}
                      </span>
                    )}
                    {!member.isTeamLeader && (
                      <button
                        onClick={() => handleSetLeader(member)}
                        disabled={submitting}
                        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title={t("SetAsTeamLeader") || "Set as team leader"}
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemove(member)}
                      disabled={submitting}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t("RemoveMember") || "Remove member"}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-2 px-4 bg-[#212121] text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t("CloseAndRefresh") || "Close & Refresh"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamEditPopup;
